import { api } from '@/lib/api';

export interface Region {
  id: string;
  parentId: string | null;
  level: 'province' | 'city' | 'village';
  name: string;
  code: string | null;
}

export interface TbmListItem {
  id: string;
  code: string;
  name: string;
  status: 'active' | 'inactive' | 'graduated';
  regionId: string | null;
  regionName: string | null;
  cityName?: string | null;
  provinceName?: string | null;
  angkatan: string | null;
  joinedPeriod: string | null;
  photoKey: string | null;
  memberCount: number;
}

export interface TbmDetail
  extends Omit<TbmListItem, 'regionName' | 'memberCount'> {
  socialMedia: string | null;
  regionPath: Region[];
  members: {
    individualId: string;
    code: string;
    name: string;
    status: string;
    photoKey: string | null;
  }[];
  assessors: { id: string; name: string; role: string }[];
}

export interface IndividualListItem {
  id: string;
  code: string;
  name: string;
  gender: 'L' | 'P' | null;
  status: 'active' | 'inactive' | 'alumni';
  photoKey: string | null;
  personalBranding: string | null;
  profession: string | null;
  lastEducation: 'SMA' | 'D3' | 'S1' | 'S2' | null;
  /** null = TBM-nya terhapus, menunggu dipasangkan ulang. */
  tbmId: string | null;
  tbmName: string | null;
  regionName: string | null;
  cityName: string | null;
  provinceName: string | null;
}

export interface IndividualDetail {
  id: string;
  code: string;
  name: string;
  gender: 'L' | 'P' | null;
  birthPlace: string | null;
  birthDate: string | null;
  phone: string | null;
  email: string | null;
  photoKey: string | null;
  personalBranding: string | null;
  profession: string | null;
  lastEducation: 'SMA' | 'D3' | 'S1' | 'S2' | null;
  status: 'active' | 'inactive' | 'alumni';
  tbmId: string | null;
  tbmName: string | null;
  tbmCode: string | null;
  regionName: string | null;
  regionPath?: Region[];
  userId?: string | null;
  userUsername?: string | null;
  userIsActive?: boolean | null;
}

export interface PageMeta {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface Paged<T> {
  data: T[];
  meta: PageMeta;
}

/** api.get() sudah membuka pembungkus `data`, jadi paginasi diambil terpisah. */
async function fetchPaged<T>(path: string): Promise<Paged<T>> {
  const res = await fetch('/api/v1' + path, { credentials: 'same-origin' });
  const body = await res.json();
  if (!res.ok) throw new Error(body?.error?.message ?? 'Gagal memuat data');
  return body as Paged<T>;
}

export const databaseApi = {
  regions: (params: { parentId?: string | null; level?: string; q?: string } = {}) => {
    const search = new URLSearchParams();
    if (params.parentId !== undefined) search.set('parentId', params.parentId ?? 'null');
    if (params.level) search.set('level', params.level);
    if (params.q) search.set('q', params.q);
    return api.get<Region[]>(`/regions?${search}`);
  },

  listTbm: (params: {
    q?: string;
    regionId?: string;
    status?: string;
    page?: number;
    pageSize?: number;
  }) => {
    const search = new URLSearchParams();
    if (params.q) search.set('q', params.q);
    if (params.regionId) search.set('regionId', params.regionId);
    if (params.status) search.set('status', params.status);
    search.set('page', String(params.page ?? 1));
    if (params.pageSize) search.set('pageSize', String(params.pageSize));
    return fetchPaged<TbmListItem>(`/tbm?${search}`);
  },

  getTbm: (id: string) => api.get<TbmDetail>(`/tbm/${id}`),
  createTbm: (body: unknown) => api.post<TbmDetail>('/tbm', body),
  updateTbm: (id: string, body: unknown) => api.patch<TbmDetail>(`/tbm/${id}`, body),
  setTbmStatus: (id: string, status: string) => api.patch(`/tbm/${id}/status`, { status }),

  listIndividuals: (params: {
    q?: string;
    tbmId?: string;
    status?: string;
    page?: number;
    pageSize?: number;
  }) => {
    const search = new URLSearchParams();
    if (params.q) search.set('q', params.q);
    if (params.tbmId) search.set('tbmId', params.tbmId);
    if (params.status) search.set('status', params.status);
    search.set('page', String(params.page ?? 1));
    if (params.pageSize) search.set('pageSize', String(params.pageSize));
    return fetchPaged<IndividualListItem>(`/individuals?${search}`);
  },

  getIndividual: (id: string) => api.get<IndividualDetail>(`/individuals/${id}`),
  createIndividual: (body: unknown) => api.post<IndividualDetail>('/individuals', body),
  updateIndividual: (id: string, body: unknown) =>
    api.patch<IndividualDetail>(`/individuals/${id}`, body),
  setIndividualStatus: (id: string, status: string) =>
    api.patch(`/individuals/${id}/status`, { status }),
  resetPelitaPassword: (individualId: string) =>
    api.post<{ success: boolean; message: string; username: string }>(
      `/users/by-individual/${individualId}/reset-password`,
    ),

  previewDeleteTbm: (ids: string[]) =>
    api.post<{
      tbm: { id: string; code: string; name: string }[];
      individuals: { id: string; name: string }[];
    }>('/tbm/delete-preview', { ids }),
  bulkDeleteTbm: (ids: string[]) =>
    api.post<{ deleted: number; orphanedIndividuals: number }>('/tbm/bulk-delete', { ids }),
  bulkDeleteIndividuals: (ids: string[]) =>
    api.post<{ deleted: number }>('/individuals/bulk-delete', { ids }),
};

export function formatMonthYear(period: string | null | undefined): string {
  if (!period) return '—';
  const match = period.match(/^(\d{4})-(\d{2})$/);
  if (match) {
    const year = match[1];
    const monthIndex = parseInt(match[2]!, 10) - 1;
    const months = [
      'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
      'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
    ];
    if (monthIndex >= 0 && monthIndex < 12) {
      return `${months[monthIndex]} ${year}`;
    }
  }
  return period;
}

export function formatDuration(period: string | null | undefined): string {
  if (!period) return '';
  const match = period.match(/^(\d{4})-(\d{2})/);
  if (!match) return '';
  const startYear = parseInt(match[1]!, 10);
  const startMonth = parseInt(match[2]!, 10);

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  const totalMonths = (currentYear - startYear) * 12 + (currentMonth - startMonth);
  if (totalMonths < 0) return 'Baru bergabung';

  const years = Math.floor(totalMonths / 12);
  const remainingMonths = totalMonths % 12;

  const parts: string[] = [];
  if (years > 0) parts.push(`${years} tahun`);
  if (remainingMonths > 0) parts.push(`${remainingMonths} bulan`);

  return parts.length > 0 ? parts.join(' ') : 'Kurang dari 1 bulan';
}
